/**
 * Rates & Commercial Policy — KAPL decision B1 (20-Oct-2026).
 *
 * KAPL: "Rate hierarchy — 1. Valid approved project-specific vendor quotation;
 * 2. Current approved dealer/project price; 3. Approved Rate Master; 4. Latest
 * validated Zoho purchase/landed cost; 5. Authorised manual budgetary
 * allowance. MRP shall be displayed only as a reference ... Supply selling
 * price = Approved cost ÷ 0.75; Installation selling price = Approved cost ÷
 * 0.77; Packing and forwarding = Applicable approved 2.5% or 3% rate ...
 * version-controlled Commercial Policy Master ... Commercial/MD approval shall
 * be required for any override."
 *
 * The page (default export) keeps the three registers: rate sources, the
 * Commercial Policy Master versions and the overrides. The named exports are
 * the pieces the project BOQ uses beside each line: the pricing detail (source,
 * validity, skipped candidates, MRP, base → ÷ → selling → P&F), the "add a
 * source for this line" form and the override request.
 *
 * Entering creates a DRAFT that prices nothing; an approver who did not enter
 * it decides (Commercial / MD; Purchase for a Zoho cost). The server enforces
 * all of it — the buttons only hide what a role cannot do.
 */

import React, { useCallback, useEffect, useState } from "react";
import { ac } from "./tableAlign";
import { StructuredValue } from "./StructuredValue";

const C = { blue: "#1F4E79", green: "#047857", amber: "#B45309", red: "#B91C1C", grey: "#6B7280",
            ink: "#111827", line: "#E5E7EB", violet: "#6D28D9" };
const th = { padding: "7px 8px", fontSize: 11, fontWeight: 700, color: C.grey, borderBottom: `1px solid ${C.line}`,
             whiteSpace: "nowrap", background: "#fff", position: "sticky", top: 0, zIndex: 1 };
const td = { padding: "6px 8px", fontSize: 12.5, borderBottom: "1px solid #F3F4F6", verticalAlign: "top" };
const input = { padding: "6px 8px", border: `1px solid ${C.line}`, borderRadius: 6, fontSize: 12.5, width: "100%",
                boxSizing: "border-box" };
const btn = (bg, fg = "#fff") => ({ padding: "4px 10px", fontSize: 11.5, fontWeight: 600, borderRadius: 6,
                                    border: bg === "#fff" ? `1px solid ${C.line}` : 0, background: bg, color: fg,
                                    cursor: "pointer", whiteSpace: "nowrap" });
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export const TIER = {
  QUOTE: ["1 · Project quotation", "#047857"],
  DEALER: ["2 · Dealer / project price", "#0E7490"],
  RATEMASTER: ["3 · Rate Master", "#1D4ED8"],
  ZOHO_COST: ["4 · Validated Zoho cost", "#6D28D9"],
  ALLOWANCE: ["5 · Manual allowance", "#B45309"],
  MRP: ["MRP — reference only", "#6B7280"],
  UNPRICED: ["Unpriced", "#DC2626"],
};
const STATE = { CURRENT: ["#ECFDF5", C.green], DRAFT: ["#F3F4F6", "#4B5563"], EXPIRED: ["#FEF2F2", C.red],
                "NOT YET VALID": ["#FFFBEB", C.amber], REJECTED: ["#FEF2F2", C.red],
                WITHDRAWN: ["#F3F4F6", C.grey], APPROVED: ["#ECFDF5", C.green], PENDING: ["#FFFBEB", C.amber],
                REVOKED: ["#F3F4F6", C.grey] };

export function TierBadge({ tier }) {
  const [label, color] = TIER[tier] || TIER.UNPRICED;
  return <span style={{ display: "inline-block", fontSize: 10.5, fontWeight: 700, color, border: `1px solid ${color}55`,
                        background: `${color}12`, borderRadius: 999, padding: "1px 7px", whiteSpace: "nowrap" }}>
    {label}</span>;
}

function Pill({ value }) {
  const [bg, fg] = STATE[value] || ["#F3F4F6", "#4B5563"];
  return <span style={{ background: bg, color: fg, borderRadius: 999, padding: "2px 8px", fontSize: 11,
                        fontWeight: 600, whiteSpace: "nowrap" }}>{value || "—"}</span>;
}

function Modal({ title, onClose, children, width = 640 }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,.45)", zIndex: 1000,
                                    display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "100%",
                                                       maxWidth: width, maxHeight: "90vh", overflowY: "auto",
                                                       padding: 18 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontWeight: 700, color: C.blue, fontSize: 15 }}>{title}</div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ ...btn("#fff", C.grey) }}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const Field = ({ label, children, half }) => (
  <label style={{ display: "block", flex: half ? "1 1 46%" : "1 1 100%", minWidth: half ? 160 : 0, marginBottom: 8 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: C.grey, marginBottom: 3 }}>{label}</div>
    {children}
  </label>
);

async function post(authFetch, url, body, method = "POST") {
  const r = await authFetch(url, { method, headers: { "Content-Type": "application/json" },
                                   body: JSON.stringify(body || {}) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : `Failed (${r.status})`);
  return d;
}

// ── A rate source: the form ─────────────────────────────────────────────────

// Today in India (IST, UTC+5:30) — validity dates are KAPL's calendar dates.
const today = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);

/** Enter one source. `preset` fixes the target (a BOQ line's SKU / element /
 *  the line itself) and the project when entered from the BOQ. */
export function SourceForm({ api, authFetch, preset = {}, tiers, onDone, onCancel }) {
  // One target at a time: the SKU, else the element, else the line itself.
  const first = preset.item_code ? "item_code" : preset.element_key ? "element_key" : preset.line_ref ? "line_ref" : null;
  const [f, setF] = useState({ tier: tiers ? tiers[0] : "RATEMASTER", component: "SUPPLY", unit: "NOS",
                               valid_from: today(), basis: "DEALER", ...preset,
                               ...(first ? { item_code: undefined, element_key: undefined, line_ref: undefined,
                                             [first]: preset[first] } : {}) });
  const [projects, setProjects] = useState([]);
  const [zoho, setZoho] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const up = (k, v) => setF(s => ({ ...s, [k]: v }));
  useEffect(() => {
    authFetch(`${api}/api/eng/projects?per_page=100`).then(r => r.json())
      .then(d => setProjects(d.items || [])).catch(() => { });
  }, [api, authFetch]);
  const t = f.tier;
  const needsProject = t === "QUOTE" || t === "ALLOWANCE" || (t === "DEALER" && f.basis === "PROJECT");
  const projectAllowed = !["RATEMASTER", "ZOHO_COST", "MRP"].includes(t);
  const targetKind = f.item_code ? "item_code" : f.element_key ? "element_key" : "line_ref";
  const findZoho = async () => {
    setErr(""); setZoho(null);
    const r = await authFetch(`${api}/api/eng/rates/zoho-candidates?item_code=${encodeURIComponent(f.item_code || "")}`);
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(d.detail || "Could not read the Zoho mirror."); return; }
    setZoho(d);
  };
  const save = async () => {
    setBusy(true); setErr("");
    const body = { ...f };
    if (!projectAllowed) delete body.project_id;
    if (t !== "DEALER") delete body.basis;
    try {
      const d = await post(authFetch, `${api}/api/eng/rates/sources`, body);
      onDone && onDone(d);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0 12px" }}>
        <Field label="Source (KAPL B1 tier)" half>
          <select value={t} onChange={e => up("tier", e.target.value)} style={input}>
            {(tiers || Object.keys(TIER).filter(k => k !== "UNPRICED")).map(k => <option key={k} value={k}>{TIER[k][0]}</option>)}
          </select>
        </Field>
        <Field label="Component" half>
          <select value={f.component} onChange={e => up("component", e.target.value)} style={input}>
            <option value="SUPPLY">Supply</option><option value="INSTALLATION">Installation</option>
          </select>
        </Field>
        {preset.line_ref || preset.element_key || preset.item_code ? (
          <Field label="For">
            <select value={targetKind} onChange={e => {
              const k = e.target.value;
              setF(s => ({ ...s, item_code: k === "item_code" ? preset.item_code : undefined,
                           element_key: k === "element_key" ? preset.element_key : undefined,
                           line_ref: k === "line_ref" ? preset.line_ref : undefined }));
            }} style={input}>
              {preset.item_code && <option value="item_code">This SKU {preset.item_code} — every line that buys it</option>}
              {preset.element_key && <option value="element_key">This element {preset.element_key} — every line of it</option>}
              {preset.line_ref && <option value="line_ref">This BOQ line only (this project)</option>}
            </select>
            {targetKind === "line_ref" && !projectAllowed && <div style={{ fontSize: 11, color: C.red, marginTop: 3 }}>
              A {TIER[t][0]} is not project-specific — choose the SKU or the element, or a project source.</div>}
          </Field>
        ) : (
          <>
            <Field label="Item code (SKU)" half>
              <input value={f.item_code || ""} onChange={e => setF(s => ({ ...s, item_code: e.target.value, element_key: "" }))}
                placeholder="A07-002.202" style={input} />
            </Field>
            <Field label="…or BOQ element key" half>
              <input value={f.element_key || ""} onChange={e => setF(s => ({ ...s, element_key: e.target.value, item_code: "" }))}
                placeholder="WALL_PANEL" style={input} disabled={t === "ZOHO_COST"} />
            </Field>
          </>
        )}
        {projectAllowed && (
          <Field label={needsProject ? "Project (required)" : "Project (optional)"} half>
            <select value={f.project_id || ""} onChange={e => up("project_id", e.target.value ? Number(e.target.value) : null)}
              style={input} disabled={!!preset.project_id}>
              <option value="">—</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.project_no} · {p.name}</option>)}
            </select>
          </Field>
        )}
        <Field label={t === "MRP" ? "MRP (₹ per unit) — reference only" : "Base cost (₹ per unit)"} half>
          <input type="number" value={f.rate ?? ""} onChange={e => up("rate", e.target.value)} style={input} />
        </Field>
        <Field label="Unit" half>
          <input value={f.unit || ""} onChange={e => up("unit", e.target.value)} placeholder="NOS / SQM / RM / LS" style={input} />
        </Field>
        {t === "QUOTE" && <>
          <Field label="Vendor" half><input value={f.vendor || ""} onChange={e => up("vendor", e.target.value)} style={input} /></Field>
          <Field label="Quotation ref" half><input value={f.quote_ref || ""} onChange={e => up("quote_ref", e.target.value)} style={input} /></Field>
          <Field label="Quotation date" half><input type="date" value={f.quote_date || ""} onChange={e => up("quote_date", e.target.value)} style={input} /></Field>
        </>}
        {(t === "DEALER" || t === "MRP") && <>
          <Field label="Brand" half><input value={f.brand || ""} onChange={e => up("brand", e.target.value)} style={input} /></Field>
          <Field label="Price list" half><input value={f.price_list || ""} onChange={e => up("price_list", e.target.value)} style={input} /></Field>
        </>}
        {t === "DEALER" && (
          <Field label="Basis" half>
            <select value={f.basis} onChange={e => up("basis", e.target.value)} style={input}>
              <option value="DEALER">Dealer price list</option><option value="PROJECT">Project band (one project)</option>
            </select>
          </Field>
        )}
        {t === "ZOHO_COST" && (
          <Field label="Zoho record (PO / bill / item purchase rate)">
            <div style={{ display: "flex", gap: 6 }}>
              <input value={f.zoho_ref || ""} onChange={e => up("zoho_ref", e.target.value)} style={input} />
              <button onClick={findZoho} style={btn("#fff", C.blue)} disabled={!f.item_code}>Find in Zoho mirror</button>
            </div>
          </Field>
        )}
        {zoho && (
          <div style={{ flex: "1 1 100%", marginBottom: 8, border: `1px solid ${C.line}`, borderRadius: 8, padding: 8 }}>
            <div style={{ fontSize: 11.5, color: C.grey, marginBottom: 6 }}>{zoho.note}</div>
            {!zoho.candidates.length && <div style={{ fontSize: 12 }}>No purchase evidence for {zoho.item_code}.</div>}
            {zoho.candidates.length > 0 && (
              <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead><tr>{["Record", "Date", "Vendor", "Rate", "Unit", ""].map(h => <th key={h} className={ac(h)} style={th}>{h}</th>)}</tr></thead>
                <tbody>{zoho.candidates.map((c, i) => (
                  <tr key={i}>
                    <td style={td}>{c.ref}{c.placeholder_shared_by && <div style={{ color: C.red, fontSize: 11 }}>
                      shared by {c.placeholder_shared_by} items — probable placeholder</div>}</td>
                    <td style={td}>{c.date || "—"}</td><td style={td}>{c.vendor || "—"}</td>
                    <td style={td}>{inr(c.rate)}</td><td style={td}>{c.unit || "—"}</td>
                    <td style={td}><button style={btn("#fff", C.blue)} onClick={() => setF(s => ({
                      ...s, rate: c.rate, unit: c.unit || s.unit, valid_from: c.date || s.valid_from,
                      zoho_ref: [c.ref, c.date, c.vendor].filter(Boolean).join(" · ") }))}>Use</button></td>
                  </tr>))}</tbody>
              </table>
            )}
          </div>
        )}
        {t !== "ZOHO_COST" && t !== "ALLOWANCE" && (
          <Field label="Document link (quotation / price list)">
            <input value={f.document_url || ""} onChange={e => up("document_url", e.target.value)} placeholder="https://…" style={input} />
          </Field>
        )}
        <Field label="Valid from" half><input type="date" value={f.valid_from || ""} onChange={e => up("valid_from", e.target.value)} style={input} /></Field>
        <Field label={["QUOTE", "DEALER", "ZOHO_COST"].includes(t) ? "Valid to (required)" : "Valid to (optional)"} half>
          <input type="date" value={f.valid_to || ""} onChange={e => up("valid_to", e.target.value)} style={input} />
        </Field>
        {t === "ALLOWANCE" && (
          <Field label="Reason (why no better source exists) — required">
            <textarea value={f.reason || ""} onChange={e => up("reason", e.target.value)} rows={2} style={input} />
          </Field>
        )}
        <Field label="Notes"><input value={f.notes || ""} onChange={e => up("notes", e.target.value)} style={input} /></Field>
      </div>
      <div style={{ fontSize: 11.5, color: C.grey, margin: "4px 0 10px" }}>
        Saved as DRAFT — it prices nothing until {t === "ZOHO_COST" ? "Purchase validates it" : "a Commercial / MD approver approves it"} (not
        you). {t === "MRP" && "An MRP is never a base cost — it is shown beside the line as a reference."}
      </div>
      {err && <div style={{ color: C.red, fontSize: 12.5, marginBottom: 8 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        {onCancel && <button onClick={onCancel} style={btn("#fff", C.grey)}>Cancel</button>}
        <button onClick={save} disabled={busy} style={btn(C.blue)}>{busy ? "Saving…" : "Save as DRAFT"}</button>
      </div>
    </div>
  );
}

/** From a BOQ line: a source for its SKU, its element, or the line itself. */
export function LineSourceModal({ api, authFetch, projectId, line, onClose, onSaved }) {
  const preset = { project_id: projectId, unit: line.unit, line_ref: line.line_ref,
                   item_code: line.item_code || undefined,
                   // A line that buys one SKU is never priced from its element key.
                   element_key: line.item_code ? undefined : (line.element_key || undefined) };
  return (
    <Modal title={`Rate source — ${line.description}`} onClose={onClose}>
      <SourceForm api={api} authFetch={authFetch} preset={preset}
        tiers={["QUOTE", "DEALER", "RATEMASTER", "ZOHO_COST", "ALLOWANCE", "MRP"]}
        onDone={() => { onSaved && onSaved(); onClose(); }} onCancel={onClose} />
    </Modal>
  );
}

/** A request to change the policy values for this project or one line. */
export function OverrideModal({ api, authFetch, projectId, line, values, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [err, setErr] = useState("");
  const up = (k, v) => setF(s => ({ ...s, [k]: v }));
  const save = async () => {
    setErr("");
    const body = { reason: f.reason, line_ref: line ? line.line_ref : null, line_label: line ? line.description : null };
    ["supply_divisor", "installation_divisor", "pf_on_cost_pct", "pf_on_price_pct"].forEach(k => {
      if (f[k] !== undefined && f[k] !== "") body[k] = Number(f[k]);
    });
    if (!line && f.apply_in_detailed) body.apply_in_detailed = true;
    try {
      await post(authFetch, `${api}/api/eng/projects/${projectId}/commercial/overrides`, body);
      onSaved && onSaved(); onClose();
    } catch (e) { setErr(e.message); }
  };
  const v = values || {};
  return (
    <Modal title={line ? `Override for one line — ${line.description}` : "Override for this project"} onClose={onClose} width={560}>
      <div style={{ fontSize: 12, color: C.grey, marginBottom: 10 }}>
        KAPL B1: any change to the Commercial Policy Master values needs Commercial / MD approval. The request applies
        only once someone other than you approves it. Leave a box empty to keep the policy value.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0 12px" }}>
        <Field label={`Supply divisor (policy ${v.supply_divisor ?? "—"})`} half>
          <input type="number" step="0.01" value={f.supply_divisor ?? ""} onChange={e => up("supply_divisor", e.target.value)} style={input} /></Field>
        <Field label={`Installation divisor (policy ${v.installation_divisor ?? "—"})`} half>
          <input type="number" step="0.01" value={f.installation_divisor ?? ""} onChange={e => up("installation_divisor", e.target.value)} style={input} /></Field>
        <Field label={`P&F % on supply cost (policy ${v.pf_on_cost_pct ?? "—"})`} half>
          <input type="number" step="0.1" value={f.pf_on_cost_pct ?? ""} onChange={e => up("pf_on_cost_pct", e.target.value)} style={input} /></Field>
        <Field label={`P&F % on supply price (policy ${v.pf_on_price_pct ?? "—"})`} half>
          <input type="number" step="0.1" value={f.pf_on_price_pct ?? ""} onChange={e => up("pf_on_price_pct", e.target.value)} style={input} /></Field>
        {!line && (
          <label style={{ flex: "1 1 100%", fontSize: 12.5, marginBottom: 8, display: "flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" checked={!!f.apply_in_detailed} onChange={e => up("apply_in_detailed", e.target.checked)} />
            Apply the Standard BOQ formulas to this project's Detailed BOQ
          </label>
        )}
        <Field label="Reason (required)"><textarea rows={2} value={f.reason || ""} onChange={e => up("reason", e.target.value)} style={input} /></Field>
      </div>
      {err && <div style={{ color: C.red, fontSize: 12.5, marginBottom: 8 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={btn("#fff", C.grey)}>Cancel</button>
        <button onClick={save} style={btn(C.blue)}>Request approval</button>
      </div>
    </Modal>
  );
}

// ── Beside a BOQ line ───────────────────────────────────────────────────────

function ComponentTrace({ label, p }) {
  if (!p) return null;
  return (
    <div style={{ flex: "1 1 300px", minWidth: 260 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.grey, marginBottom: 4 }}>{label}</div>
      {p.priced ? (
        <div style={{ fontSize: 12.5 }}>
          <TierBadge tier={p.source} /> <b>{inr(p.rate)}</b> · {p.ref}
          <div style={{ color: C.grey, fontSize: 11.5 }}>
            valid {p.valid_from || "—"} → {p.valid_to || "open"} · approved by {p.approved_by || "—"}
            {p.document_url && <> · <a href={p.document_url} target="_blank" rel="noreferrer">document</a></>}
          </div>
        </div>
      ) : <div style={{ fontSize: 12.5, color: C.red }}>{p.lapsed || "No approved, valid source — UNPRICED"}</div>}
      {(p.skipped || []).length > 0 && (
        <ul style={{ margin: "4px 0 0", paddingLeft: 16, fontSize: 11.5, color: C.amber }}>
          {p.skipped.map(s => <li key={s.id}>{s.ref} ({inr(s.rate)}/{s.unit}) skipped — {s.reason}</li>)}
        </ul>
      )}
    </div>
  );
}

/** The whole story of one line's price. */
export function LinePricing({ line, commercial }) {
  const pr = line.pricing || {};
  const c = line.commercial;
  return (
    <div style={{ background: "#F9FAFB", border: `1px solid ${C.line}`, borderRadius: 8, padding: 10 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
        <ComponentTrace label="SUPPLY — base cost" p={pr.supply} />
        <ComponentTrace label="INSTALLATION — base cost" p={pr.installation} />
      </div>
      {(pr.mrp || []).length > 0 && (
        <div style={{ fontSize: 11.5, color: C.grey, marginTop: 6 }}>
          MRP (reference only, never a base cost): {pr.mrp.map(m => `${inr(m.mrp)}/${m.unit} ${m.ref} (${m.state})`).join(" · ")}
        </div>
      )}
      {c ? (
        <div style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.7 }}>
          <div>Supply: {inr(c.supply_cost)} ÷ {c.supply_divisor} = <b>{inr(c.supply_selling)}</b>
            {" "}· P&F {c.pf_on_cost_pct}% of cost {inr(c.pf_on_cost)} ÷ {c.supply_divisor} = {inr(c.pf_on_cost_selling)}
            {" "}→ supply price {inr(c.supply_quote)}</div>
          <div>P&F {c.pf_on_price_pct}% of supply price = <b>{inr(c.pf_on_price)}</b></div>
          <div>Installation: {inr(c.installation_cost)} ÷ {c.installation_divisor} = <b>{inr(c.installation_selling)}</b></div>
          <div style={{ fontWeight: 700, color: C.blue }}>Selling value {inr(c.selling_total)}</div>
          {Object.keys(c.overridden || {}).length > 0 && (
            <div style={{ color: C.violet, fontSize: 11.5 }}>Approved overrides (field → request #):{" "}
              <StructuredValue value={c.overridden} /></div>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 8, fontSize: 11.5, color: C.grey }}>
          {commercial && commercial.applied === false ? (commercial.reason || "Cost only.")
            : "No selling value — the line has no priced component."}
        </div>
      )}
    </div>
  );
}

/** Base cost → divisor → selling → P&F for the whole BOQ (Standard REF BOQ SUMMARY). */
export function BoqCommercialCard({ commercial, costTotal, onOverride }) {
  if (!commercial) return null;
  const v = commercial.project_values || commercial.policy || {};
  const t = commercial.totals || {};
  const rows = commercial.applied ? [
    ["Supply base cost", t.supply_cost], [`P&F ${v.pf_on_cost_pct}% on supply cost`, t.pf_on_cost],
    [`Supply selling (cost ÷ ${v.supply_divisor})`, t.supply_selling],
    [`P&F on cost ÷ ${v.supply_divisor}`, t.pf_on_cost_selling], ["Supply quotation price", t.supply_quote],
    [`P&F ${v.pf_on_price_pct}% of supply price`, t.pf_on_price],
    ["Installation base cost", t.installation_cost],
    [`Installation selling (cost ÷ ${v.installation_divisor})`, t.installation_selling],
  ] : [];
  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 10, padding: 12, background: "#fff", marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 700, color: C.blue }}>Commercial — KAPL B1</div>
        {commercial.policy && <Pill value={`Policy v${commercial.policy.version}`} />}
        <Pill value={commercial.stage} />
        {commercial.overrides_pending > 0 && <span style={{ fontSize: 11.5, color: C.amber }}>
          {commercial.overrides_pending} override request(s) awaiting Commercial / MD</span>}
        <div style={{ flex: 1 }} />
        {onOverride && <button onClick={onOverride} style={btn("#fff", C.violet)}>Request override…</button>}
      </div>
      {commercial.note && <div style={{ fontSize: 11.5, color: C.amber, marginTop: 4 }}>{commercial.note}</div>}
      {!commercial.applied ? (
        <div style={{ fontSize: 12.5, color: C.grey, marginTop: 6 }}>{commercial.reason} Base cost total {inr(costTotal)}.</div>
      ) : (
        <table style={{ borderCollapse: "collapse", marginTop: 8, minWidth: 360 }}>
          <thead><tr>{["Calculation", "Amount ₹"].map(h => <th key={h} className={ac(h)} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map(([k, x]) => <tr key={k}><td style={td}>{k}</td><td style={td}>{inr(x)}</td></tr>)}
            <tr><td style={{ ...td, fontWeight: 800, color: C.blue }}>Grand total (excl. GST)</td>
              <td style={{ ...td, fontWeight: 800, color: C.green }}>{inr(t.grand_total)}</td></tr>
          </tbody>
        </table>
      )}
      {commercial.applied && Object.keys((commercial.project_values || {}).overridden || {}).length > 0 && (
        <div style={{ fontSize: 11.5, color: C.violet, marginTop: 4 }}>
          Project overrides in force (field → request #): <StructuredValue value={commercial.project_values.overridden} />
        </div>
      )}
      {commercial.allowance_lines > 0 && <div style={{ fontSize: 11.5, color: C.amber, marginTop: 4 }}>
        {commercial.allowance_lines} line(s) rest on a manual budgetary allowance — budgetary only.</div>}
    </div>
  );
}

// ── The registers ───────────────────────────────────────────────────────────

function Sources({ api, authFetch, can, me }) {
  const [rows, setRows] = useState([]);
  const [tier, setTier] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (tier) p.set("tier", tier);
    if (status) p.set("status", status);
    if (q.trim()) p.set("q", q.trim());
    authFetch(`${api}/api/eng/rates/sources?${p}`).then(r => r.json()).then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]));
  }, [api, authFetch, tier, status, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const act = async (s, action) => {
    let note = null;
    if (action !== "approve") {
      note = window.prompt(`Reason to ${action} source #${s.id}:`);
      if (!note) return;
    }
    setMsg("");
    try { await post(authFetch, `${api}/api/eng/rates/sources/${s.id}/${action}`, { note }); load(); }
    catch (e) { setMsg(e.message); }
  };
  const mayDecide = (s) => s.status === "DRAFT" && s.created_by !== me
    && (s.tier === "ZOHO_COST" ? can("rates.validate_zoho") : can("commercial.approve"));
  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <select value={tier} onChange={e => setTier(e.target.value)} style={{ ...input, width: 220 }}>
          <option value="">Every source</option>
          {Object.keys(TIER).filter(k => k !== "UNPRICED").map(k => <option key={k} value={k}>{TIER[k][0]}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...input, width: 150 }}>
          <option value="">Any status</option>
          {["DRAFT", "APPROVED", "REJECTED", "WITHDRAWN"].map(s => <option key={s}>{s}</option>)}
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search SKU, element, vendor, brand, ref…"
          style={{ ...input, flex: 1, minWidth: 200 }} />
        {can("rates.enter") && <button onClick={() => setAdding(true)} style={btn(C.blue)}>+ New source</button>}
      </div>
      {msg && <div style={{ color: C.red, fontSize: 12.5, marginBottom: 8 }}>{msg}</div>}
      <div style={{ overflowX: "auto", border: `1px solid ${C.line}`, borderRadius: 8, maxHeight: "65vh", overflowY: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>{["#", "Source", "Component", "For", "Project", "Rate ₹", "Unit", "Reference", "Validity",
                         "Status", "Entered by", "Decided by", ""].map(h => <th key={h} className={ac(h)} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map(s => (
              <tr key={s.id}>
                <td style={td}>{s.id}</td>
                <td style={td}><TierBadge tier={s.tier} /></td>
                <td style={td}>{s.component === "INSTALLATION" ? "Installation" : "Supply"}</td>
                <td style={td}>{s.item_code || s.element_key || <span title={s.line_ref}>one BOQ line</span>}</td>
                <td style={td}>{s.project_id || "—"}</td>
                <td style={td}>{inr(s.rate)}</td>
                <td style={td}>{s.unit}</td>
                <td style={td}>{s.ref}{s.document_url && <> · <a href={s.document_url} target="_blank" rel="noreferrer">doc</a></>}
                  {s.reason && <div style={{ fontSize: 11, color: C.grey }}>{s.reason}</div>}</td>
                <td style={td}>{s.valid_from || "—"} → {s.valid_to || "open"}</td>
                <td style={td}><Pill value={s.state} /></td>
                <td style={td}>{s.created_by}</td>
                <td style={td}>{s.approved_by || "—"}{s.decided_note && <div style={{ fontSize: 11, color: C.grey }}>{s.decided_note}</div>}</td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>
                  {mayDecide(s) && <>
                    <button onClick={() => act(s, "approve")} style={btn(C.green)}>{s.tier === "ZOHO_COST" ? "Validate" : "Approve"}</button>{" "}
                    <button onClick={() => act(s, "reject")} style={btn("#fff", C.red)}>Reject</button>{" "}
                  </>}
                  {["DRAFT", "APPROVED"].includes(s.status) && (can("commercial.approve") || (s.created_by === me && can("rates.enter"))) &&
                    <button onClick={() => act(s, "withdraw")} style={btn("#fff", C.grey)}>Withdraw</button>}
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td style={td} colSpan={13}>No sources match.</td></tr>}
          </tbody>
        </table>
      </div>
      {adding && <Modal title="New rate source" onClose={() => setAdding(false)}>
        <SourceForm api={api} authFetch={authFetch} onDone={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
      </Modal>}
    </div>
  );
}

function Policy({ api, authFetch, can, me }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => authFetch(`${api}/api/eng/commercial/policies`).then(r => r.json())
    .then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([])), [api, authFetch]);
  useEffect(() => { load(); }, [load]);
  const cur = rows.find(r => r.current);
  const act = async (p, action) => {
    let note = null;
    if (action === "reject") { note = window.prompt("Reason to reject:"); if (!note) return; }
    setMsg("");
    try { await post(authFetch, `${api}/api/eng/commercial/policies/${p.id}/${action}`, { note }); load(); }
    catch (e) { setMsg(e.message); }
  };
  const save = async () => {
    setMsg("");
    try { await post(authFetch, `${api}/api/eng/commercial/policies`, f); setF(null); load(); }
    catch (e) { setMsg(e.message); }
  };
  return (
    <div>
      <div style={{ fontSize: 12.5, color: C.grey, marginBottom: 10, maxWidth: 900, lineHeight: 1.5 }}>
        A BOQ keeps the version it was generated with; regenerating takes the version in force. Budgetary BOQs apply the
        formulas automatically; a Detailed BOQ shows base cost only unless the version (or an approved project override)
        says to apply them. A new version is a DRAFT until a Commercial / MD approver other than its author approves it.
      </div>
      {can("commercial.approve") && !f && (
        <button style={{ ...btn(C.blue), marginBottom: 10 }} onClick={() => setF({
          effective_from: today(), supply_divisor: cur?.supply_divisor, installation_divisor: cur?.installation_divisor,
          pf_on_cost_pct: cur?.pf_on_cost_pct, pf_on_price_pct: cur?.pf_on_price_pct, apply_in_detailed: false,
          pf_rule: cur?.pf_rule, notes: "" })}>Draft a new version…</button>
      )}
      {f && (
        <div style={{ border: `1px solid ${C.line}`, borderRadius: 8, padding: 12, marginBottom: 12, maxWidth: 720 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0 12px" }}>
            <Field label="Effective from" half><input type="date" value={f.effective_from} onChange={e => setF({ ...f, effective_from: e.target.value })} style={input} /></Field>
            <Field label="Supply divisor" half><input type="number" step="0.01" value={f.supply_divisor ?? ""} onChange={e => setF({ ...f, supply_divisor: e.target.value })} style={input} /></Field>
            <Field label="Installation divisor" half><input type="number" step="0.01" value={f.installation_divisor ?? ""} onChange={e => setF({ ...f, installation_divisor: e.target.value })} style={input} /></Field>
            <Field label="P&F % on supply cost" half><input type="number" step="0.1" value={f.pf_on_cost_pct ?? ""} onChange={e => setF({ ...f, pf_on_cost_pct: e.target.value })} style={input} /></Field>
            <Field label="P&F % on supply quotation price" half><input type="number" step="0.1" value={f.pf_on_price_pct ?? ""} onChange={e => setF({ ...f, pf_on_price_pct: e.target.value })} style={input} /></Field>
            <label style={{ flex: "1 1 46%", fontSize: 12.5, display: "flex", gap: 6, alignItems: "center" }}>
              <input type="checkbox" checked={!!f.apply_in_detailed} onChange={e => setF({ ...f, apply_in_detailed: e.target.checked })} />
              Apply to Detailed BOQs too</label>
            <Field label="P&F applicability"><textarea rows={2} value={f.pf_rule || ""} onChange={e => setF({ ...f, pf_rule: e.target.value })} style={input} /></Field>
            <Field label="What changed and why (required)"><textarea rows={2} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} style={input} /></Field>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={() => setF(null)} style={btn("#fff", C.grey)}>Cancel</button>
            <button onClick={save} style={btn(C.blue)}>Save DRAFT</button>
          </div>
        </div>
      )}
      {msg && <div style={{ color: C.red, fontSize: 12.5, marginBottom: 8 }}>{msg}</div>}
      <div style={{ overflowX: "auto", border: `1px solid ${C.line}`, borderRadius: 8 }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>{["Version", "Effective date", "Supply ÷", "Installation ÷", "P&F % on cost", "P&F % on price",
                         "Detailed", "Status", "P&F applicability / source", "Approved by", ""].map(h =>
            <th key={h} className={ac(h)} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map(p => (
              <tr key={p.id} style={{ background: p.current ? "#F0FDF4" : undefined }}>
                <td style={td}>v{p.version}{p.current && <div style={{ fontSize: 10.5, color: C.green, fontWeight: 700 }}>IN FORCE</div>}</td>
                <td style={td}>{p.effective_from}</td>
                <td style={td}>{p.supply_divisor}</td><td style={td}>{p.installation_divisor}</td>
                <td style={td}>{p.pf_on_cost_pct}</td><td style={td}>{p.pf_on_price_pct}</td>
                <td style={td}>{p.apply_in_detailed ? "applied" : "cost only"}</td>
                <td style={td}><Pill value={p.status} /></td>
                <td style={{ ...td, maxWidth: 420, fontSize: 11.5 }}>{p.pf_rule}<div style={{ color: C.grey }}>{p.source}</div>
                  {p.notes && <div style={{ color: C.grey, whiteSpace: "pre-wrap" }}>{p.notes}</div>}</td>
                <td style={td}>{p.approved_by || "—"}</td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>
                  {p.status === "DRAFT" && can("commercial.approve") && p.created_by !== me && <>
                    <button onClick={() => act(p, "approve")} style={btn(C.green)}>Approve</button>{" "}
                    <button onClick={() => act(p, "reject")} style={btn("#fff", C.red)}>Reject</button></>}
                  {p.status === "DRAFT" && p.created_by === me &&
                    <button onClick={() => act(p, "withdraw")} style={btn("#fff", C.grey)}>Withdraw</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Overrides({ api, authFetch, can, me }) {
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => authFetch(`${api}/api/eng/commercial/overrides`).then(r => r.json())
    .then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([])), [api, authFetch]);
  useEffect(() => { load(); }, [load]);
  const act = async (o, action) => {
    let note = null;
    if (action !== "approve" && action !== "withdraw") { note = window.prompt(`Reason to ${action}:`); if (!note) return; }
    setMsg("");
    try { await post(authFetch, `${api}/api/eng/commercial/overrides/${o.id}/${action}`, { note }); load(); }
    catch (e) { setMsg(e.message); }
  };
  const changes = (o) => {
    const d = {};
    ["supply_divisor", "installation_divisor", "pf_on_cost_pct", "pf_on_price_pct", "apply_in_detailed"].forEach(k => {
      if (o[k] !== null && o[k] !== undefined) d[k] = o[k];
    });
    return d;
  };
  return (
    <div>
      {msg && <div style={{ color: C.red, fontSize: 12.5, marginBottom: 8 }}>{msg}</div>}
      <div style={{ overflowX: "auto", border: `1px solid ${C.line}`, borderRadius: 8 }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>{["#", "Project", "Scope", "Change", "Reason", "Status", "Requested by", "Decided by", ""].map(h =>
            <th key={h} className={ac(h)} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map(o => (
              <tr key={o.id}>
                <td style={td}>{o.id}</td><td style={td}>{o.project_id}</td>
                <td style={td}>{o.line_ref ? (o.line_label || "one line") : "whole project"}</td>
                <td style={td}><StructuredValue value={changes(o)} /></td>
                <td style={td}>{o.reason}</td>
                <td style={td}><Pill value={o.status} /></td>
                <td style={td}>{o.requested_by}</td>
                <td style={td}>{o.decided_by || "—"}{o.decided_note && <div style={{ fontSize: 11, color: C.grey }}>{o.decided_note}</div>}</td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>
                  {o.status === "PENDING" && can("commercial.approve") && o.requested_by !== me && <>
                    <button onClick={() => act(o, "approve")} style={btn(C.green)}>Approve</button>{" "}
                    <button onClick={() => act(o, "reject")} style={btn("#fff", C.red)}>Reject</button></>}
                  {o.status === "PENDING" && o.requested_by === me &&
                    <button onClick={() => act(o, "withdraw")} style={btn("#fff", C.grey)}>Withdraw</button>}
                  {o.status === "APPROVED" && can("commercial.approve") &&
                    <button onClick={() => act(o, "revoke")} style={btn("#fff", C.red)}>Revoke</button>}
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td style={td} colSpan={9}>No override requests.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function RatesPolicy({ api, authFetch, can, me }) {
  const [tab, setTab] = useState("sources");
  const tabs = [["sources", "Rate sources"], ["policy", "Commercial Policy Master"], ["overrides", "Overrides"]];
  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: C.ink, marginBottom: 4 }}>Rates & Commercial Policy</h2>
      <p style={{ fontSize: 13, color: C.grey, marginBottom: 12, maxWidth: 900, lineHeight: 1.5 }}>
        KAPL decision B1 (20-Oct-2026). Each BOQ line takes the first approved, in-date base cost in this order:
        1 project vendor quotation → 2 dealer / project price → 3 Rate Master → 4 validated Zoho purchase / landed
        cost → 5 authorised manual budgetary allowance. MRP is shown as a reference only, never a base cost. Nothing
        valid → the line stays UNPRICED.
      </p>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...btn(tab === k ? C.blue : "#fff", tab === k ? "#fff" : C.blue),
                                                              padding: "6px 14px", fontSize: 12.5 }}>{label}</button>
        ))}
      </div>
      {tab === "sources" && <Sources api={api} authFetch={authFetch} can={can} me={me} />}
      {tab === "policy" && <Policy api={api} authFetch={authFetch} can={can} me={me} />}
      {tab === "overrides" && <Overrides api={api} authFetch={authFetch} can={can} me={me} />}
    </div>
  );
}
